import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ItodoListItem } from '@typings/pages/ItodoListItem.interface';
import TodoCheckbox from '@components/dataEntry/checkbox/TodoCheckbox';
import Label from '@components/dataEntry/Input/Label';
import { scrollbarHide } from '@styles/variants/scrollbar';

const Container = styled.div`
  width: 100%;
  padding: 16px;
  background: var(--subColor);
  height: 100%;
`;

const Box = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--cardBackgroundColor);
  border-radius: 12px;
  padding: 8px 16px;
  overflow-y: auto;
  ${scrollbarHide}
`;

const AddItemBtn = styled.button`
  color: var(--subTextColor);
  padding: 4px 6px;
  border-radius: var(--buttonBorderRadius);
  transition: all 0.2s;
  font-size: 12px;

  &:hover {
    background-color: var(--subColor);
    color: var(--textColor);
  }
`;

const TodoItemWrapper = styled.div`
  margin-bottom: 8px;
`;

const STORAGE_KEY = 'daily-todo-list';

interface DailyTodoListProps {
  label?: string;
  showLabel?: boolean;
}

function DailyTodoList({
  label = 'Daily Todo List',
  showLabel = true,
}: DailyTodoListProps) {
  const [todoList, setTodoList] = useState<ItodoListItem[]>([]);

  // localStorage에서 데이터 로드
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setTodoList(parsed);
      } catch (error) {
        console.error('투두리스트 로드 실패:', error);
        setTodoList([]);
      }
    }
  }, []);

  // localStorage에 데이터 저장
  const saveTodoList = useCallback((list: ItodoListItem[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      setTodoList(list);
    } catch (error) {
      console.error('투두리스트 저장 실패:', error);
    }
  }, []);

  // 새 항목 추가
  const handleAddItem = useCallback(() => {
    const newItem: ItodoListItem = {
      id: `todo-${Date.now()}`,
      contents: '',
      order: todoList.length,
      isChecked: false,
    };
    saveTodoList([...todoList, newItem]);
  }, [todoList, saveTodoList]);

  // 항목 업데이트
  const handleUpdateItem = useCallback(
    (id: string, updates: Partial<ItodoListItem>) => {
      const updatedList = todoList.map((item) =>
        item.id === id ? { ...item, ...updates } : item,
      );
      saveTodoList(updatedList);
    },
    [todoList, saveTodoList],
  );

  // 항목 삭제
  const handleDeleteItem = useCallback(
    (id: string) => {
      const filteredList = todoList.filter((item) => item.id !== id);
      // order 재정렬
      const reorderedList = filteredList.map((item, index) => ({
        ...item,
        order: index,
      }));
      saveTodoList(reorderedList);
    },
    [todoList, saveTodoList],
  );

  return (
    <Container>
      <Box>
        {showLabel && (
          <Label size="sm" style={{ marginBottom: '16px' }}>
            {label}
          </Label>
        )}
        <div>
          {todoList.map((item) => (
            <TodoItemWrapper key={item.id}>
              <LocalTodoItem
                data={item}
                onUpdate={handleUpdateItem}
                onDelete={handleDeleteItem}
              />
            </TodoItemWrapper>
          ))}
        </div>
        <AddItemBtn onClick={handleAddItem}>+ 항목 추가</AddItemBtn>
      </Box>
    </Container>
  );
}

// 로컬 투두 아이템 컴포넌트
interface LocalTodoItemProps {
  data: ItodoListItem;
  onUpdate: (id: string, updates: Partial<ItodoListItem>) => void;
  onDelete: (id: string) => void;
}

const LocalTodoItem = React.memo(
  ({ data, onUpdate, onDelete }: LocalTodoItemProps) => {
    const [value, setValue] = useState(data.contents || '');

    useEffect(() => {
      setValue(data.contents || '');
    }, [data.contents]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
      },
      [],
    );

    const handleSave = useCallback(() => {
      if (data.id) {
        onUpdate(data.id, { contents: value });
      }
    }, [data.id, value, onUpdate]);

    const handleCheckChange = useCallback(() => {
      if (data.id) {
        onUpdate(data.id, { isChecked: !data.isChecked });
      }
    }, [data.id, data.isChecked, onUpdate]);

    const handleDelete = useCallback(() => {
      if (data.id) {
        onDelete(data.id);
      }
    }, [data.id, onDelete]);

    return (
      <TodoCheckbox
        id={data.id || ''}
        value={value}
        onChange={handleChange}
        checked={data.isChecked || false}
        checkBoxChange={handleCheckChange}
        placeholder="할 일 입력"
        isEdit={true}
        handleSave={handleSave}
        handleDelete={handleDelete}
      />
    );
  },
);

LocalTodoItem.displayName = 'LocalTodoItem';

export default DailyTodoList;
